import React, { useState, useEffect, useMemo } from 'react';
import {
  Calendar as CalendarIcon,
  Clock,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  MapPin
} from 'lucide-react';
import { Reservation, SiteSettings } from '../types';
import {
  checkDateAvailability,
  getAvailableCollectionDates,
  getEarliestAvailableCollectionDate,
  toIsoDateString
} from '../services/availabilityEngine';

interface CollectionDatePickerProps {
  selectedDate: string; // e.g. "Wednesday, 19 August 2026 (09:00–15:00)" or formatted date string
  onSelectDate: (formattedDateString: string, rawDateIso: string) => void;
  siteSettings?: SiteSettings;
  existingReservations?: Reservation[];
  bookingWindowDays?: number;
}

/**
 * Extract clean base date (strips parenthetical time hours or bullet points)
 */
function extractBaseDate(str: string): string {
  if (!str) return '';
  return str.split(' • ')[0].split(' (')[0].trim();
}

export const CollectionDatePicker: React.FC<CollectionDatePickerProps> = ({
  selectedDate,
  onSelectDate,
  siteSettings,
  existingReservations = [],
  bookingWindowDays
}) => {
  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  const windowDays = bookingWindowDays || siteSettings?.bookingWindowDays || 30;

  // Automatically calculate Earliest Collection Date = Today + Minimum Processing Days
  // If closed on that day, engine advances to the next available open day
  const earliestAvailable = useMemo(() => {
    return getEarliestAvailableCollectionDate({
      siteSettings,
      existingReservations,
      bookingWindowDays: windowDays
    });
  }, [siteSettings, existingReservations, windowDays]);

  useEffect(() => {
    getAvailableCollectionDates({
      siteSettings,
      existingReservations,
      currentReservationDate: selectedDate,
      bookingWindowDays: windowDays
    });
  }, [siteSettings, existingReservations, selectedDate, windowDays]);

  const maxDate = useMemo(() => {
    const m = new Date(today);
    m.setDate(m.getDate() + windowDays);
    return m;
  }, [today, windowDays]);

  const initialMonthDate = useMemo(() => {
    if (earliestAvailable) {
      return new Date(earliestAvailable.date.getFullYear(), earliestAvailable.date.getMonth(), 1);
    }
    return new Date(today.getFullYear(), today.getMonth(), 1);
  }, [earliestAvailable, today]);

  const [currentMonth, setCurrentMonth] = useState<Date>(initialMonthDate);

  // Calendar calculations
  const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
  const endOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

  const startDayOfWeek = startOfMonth.getDay(); // 0 is Sunday
  const paddingDays = (startDayOfWeek + 6) % 7; // Monday is 0

  const daysInMonth: Date[] = [];
  for (let i = 1; i <= endOfMonth.getDate(); i++) {
    daysInMonth.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i));
  }

  const prevMonth = () => {
    const prev = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    if (prev >= new Date(today.getFullYear(), today.getMonth(), 1)) {
      setCurrentMonth(prev);
    }
  };

  const nextMonth = () => {
    const next = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    if (next <= maxDate) {
      setCurrentMonth(next);
    }
  };

  // Selected date parsing
  const baseSelectedString = extractBaseDate(selectedDate);

  // Find status of the actively selected date
  const activeSelectedDateObj = useMemo(() => {
    if (!baseSelectedString) return null;
    const d = new Date(baseSelectedString);
    return isNaN(d.getTime()) ? null : d;
  }, [baseSelectedString]);

  const activeStatus = useMemo(() => {
    if (!activeSelectedDateObj) return null;
    return checkDateAvailability(activeSelectedDateObj, {
      siteSettings,
      existingReservations
    });
  }, [activeSelectedDateObj, siteSettings, existingReservations]);

  // Date click handler: Selects the day with opening hours
  const handleDateClick = (date: Date) => {
    const status = checkDateAvailability(date, { siteSettings, existingReservations });
    if (!status.isAvailable) return;

    const formattedWithHours = `${status.formattedDate} (${status.openTime}–${status.closeTime})`;
    onSelectDate(formattedWithHours, status.isoDate);
  };

  // Quick select earliest/next available date handler
  const handleSelectEarliestDate = () => {
    if (!earliestAvailable) return;
    const formattedWithHours = `${earliestAvailable.formattedDate} (${earliestAvailable.openTime}–${earliestAvailable.closeTime})`;
    onSelectDate(formattedWithHours, earliestAvailable.isoDate);
  };

  const monthYearHeader = currentMonth.toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric'
  });

  const locationName = siteSettings?.collectionLocation || 'Bert Boyden Community Centre';
  const locationAddress = siteSettings?.collectionAddress || '34 Carver Road, Immingham DN40 1DS';

  const nextAvailableLabel = useMemo(() => {
    if (!earliestAvailable) return null;
    return earliestAvailable.date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  }, [earliestAvailable]);

  return (
    <div className="space-y-2.5">
      {/* 1. CLEAN HEADER WITH EMBEDDED NEXT AVAILABLE BADGE */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <CalendarIcon className="w-4 h-4 text-[#002366] shrink-0" />
          <span className="font-heading font-extrabold text-sm text-slate-800">
            Choose Collection Day
          </span>
        </div>

        {earliestAvailable && (
          <button
            type="button"
            onClick={handleSelectEarliestDate}
            title="Click to select next available collection day"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50/90 hover:bg-blue-100/90 border border-blue-200/80 text-blue-950 transition shadow-2xs cursor-pointer group shrink-0"
          >
            <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
            <span className="text-[11px] font-medium text-slate-600">Next Available:</span>
            <span className="text-[11px] font-bold text-[#002366] group-hover:text-blue-700">
              {nextAvailableLabel}
            </span>
          </button>
        )}
      </div>

      {/* 2. CALENDAR CARD */}
      <div className="bg-white border border-slate-200/90 rounded-2xl p-4 sm:p-5 space-y-3 shadow-xs">
        {/* Month Navigation */}
        <div className="flex items-center justify-between gap-2 pb-1 border-b border-slate-100">
          <button
            type="button"
            onClick={prevMonth}
            className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 transition cursor-pointer"
            aria-label="Previous month"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="font-bold text-xs text-slate-800 px-2 text-center select-none uppercase tracking-wide">
            {monthYearHeader}
          </span>
          <button
            type="button"
            onClick={nextMonth}
            className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 transition cursor-pointer"
            aria-label="Next month"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Days of Week Header */}
        <div className="grid grid-cols-7 gap-1 text-center font-bold text-[10px] text-slate-400 uppercase tracking-wider">
          <span>Mon</span>
          <span>Tue</span>
          <span>Wed</span>
          <span>Thu</span>
          <span>Fri</span>
          <span>Sat</span>
          <span>Sun</span>
        </div>

        {/* Days Grid */}
        <div className="grid grid-cols-7 gap-1">
          {/* Padding cells */}
          {Array.from({ length: paddingDays }).map((_, idx) => (
            <div key={`pad-${idx}`} className="h-9 sm:h-10 rounded-xl bg-transparent" />
          ))}

          {daysInMonth.map((date) => {
            const status = checkDateAvailability(date, { siteSettings, existingReservations });
            const formattedCellDate = status.formattedDate;
            const isoCellDate = status.isoDate;

            const isSelected =
              !!baseSelectedString &&
              (baseSelectedString === formattedCellDate ||
                baseSelectedString === isoCellDate ||
                toIsoDateString(date) === toIsoDateString(new Date(baseSelectedString)));

            return (
              <button
                key={date.toISOString()}
                type="button"
                disabled={!status.isAvailable}
                onClick={() => handleDateClick(date)}
                className={`h-9 sm:h-10 rounded-xl font-bold text-xs flex flex-col items-center justify-center transition relative ${
                  isSelected
                    ? 'bg-[#002366] text-white shadow-md ring-2 ring-blue-500 z-10'
                    : status.isAvailable
                    ? 'bg-emerald-50 text-emerald-900 border border-emerald-200 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 cursor-pointer shadow-2xs'
                    : status.isPast || status.isWithinProcessingPeriod
                    ? 'bg-slate-100/80 text-slate-300 cursor-not-allowed line-through'
                    : 'bg-slate-100/50 text-slate-300 border border-transparent cursor-not-allowed'
                }`}
                title={
                  status.isAvailable
                    ? `Open: ${status.openTime} – ${status.closeTime}`
                    : status.reason || 'Unavailable'
                }
              >
                <span>{date.getDate()}</span>
                {status.isAvailable && !isSelected && (
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 absolute bottom-1" />
                )}
              </button>
            );
          })}
        </div>

        {/* 3. CALENDAR LEGEND */}
        <div className="flex items-center justify-center gap-4 sm:gap-6 pt-2 pb-0.5 text-[11px] font-semibold text-slate-600 border-t border-slate-100">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-emerald-100 shrink-0" />
            <span>Available</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-[#002366] ring-2 ring-blue-100 shrink-0" />
            <span>Selected</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-slate-300 shrink-0" />
            <span>Unavailable</span>
          </div>
        </div>
      </div>

      {/* 4. DYNAMIC INFORMATION CARD (Displayed once a date is selected) */}
      {activeStatus && activeStatus.isAvailable && (
        <div className="p-4 bg-blue-50/80 border border-blue-200/90 rounded-2xl space-y-2.5 shadow-xs animate-fadeIn">
          <div className="flex items-start justify-between gap-2 border-b border-blue-200/60 pb-2.5">
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs text-blue-900 font-extrabold">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span className="text-sm font-extrabold text-[#002366]">
                  {activeStatus.formattedDate}
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-blue-800 font-bold">
                <Clock className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                <span>Opening Hours: {activeStatus.openTime}–{activeStatus.closeTime}</span>
              </div>
            </div>

            <div className="text-right shrink-0">
              <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full border border-emerald-200">
                Drop-in
              </span>
            </div>
          </div>

          <div className="flex items-start gap-1.5 text-[11px] text-slate-600">
            <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-slate-800">
                {locationName}
              </p>
              <p className="text-[10px] text-slate-500">
                {locationAddress}
              </p>
            </div>
          </div>

          <div className="p-2.5 bg-white/90 rounded-xl border border-blue-100 text-[11px] text-slate-600 leading-relaxed">
            Please collect your items any time during these opening hours. If you have a preferred collection time, you can include it in the notes below.
          </div>
        </div>
      )}
    </div>
  );
};
